import type { AIMessage, AIResponse } from './aiService';

interface MockResponseTemplate {
  keywords: string[];
  response: string;
  delay?: number;
}

const MOCK_RESPONSES: MockResponseTemplate[] = [
  {
    keywords: ['morning', 'routine', 'wake up', 'start day'],
    response: `I can help you create an energizing morning routine! Based on best practices, here's a structured morning routine:

{
  "type": "routine",
  "name": "Energizing Morning Routine",
  "description": "Start your day with intention and energy",
  "category": "personal",
  "habits": [
    {"name": "Wake up at consistent time", "duration": 1, "order": 0},
    {"name": "Drink water (16oz)", "duration": 2, "order": 1},
    {"name": "Morning stretching", "duration": 5, "order": 2},
    {"name": "Meditation or breathwork", "duration": 10, "order": 3},
    {"name": "Healthy breakfast", "duration": 15, "order": 4},
    {"name": "Review daily goals", "duration": 5, "order": 5}
  ]
}

This routine takes about 38 minutes and sets a positive tone for the day. Would you like me to adjust the timing or add any specific activities?`,
    delay: 2000
  },
  {
    keywords: ['fitness', 'exercise', 'workout', 'gym', 'health'],
    response: `Great goal! Let me create a comprehensive fitness program for you:

{
  "type": "program",
  "name": "Complete Fitness Journey",
  "description": "Build strength, endurance, and healthy habits",
  "habits": [
    {"name": "Morning workout", "description": "30 minutes of exercise", "category": "personal", "interval": "daily", "icon": "Dumbbell"},
    {"name": "Track water intake", "description": "8 glasses per day", "category": "personal", "interval": "daily", "icon": "Droplet"},
    {"name": "Meal prep Sunday", "description": "Prepare healthy meals", "category": "personal", "interval": "weekly", "icon": "Salad"}
  ],
  "projects": [
    {
      "name": "Fitness Foundation Program",
      "description": "12-week transformation program",
      "category": "personal",
      "phases": [
        {"name": "Assessment & Planning", "tasks": ["Take initial measurements", "Set SMART goals", "Choose workout split", "Plan nutrition strategy"]},
        {"name": "Building Base (Weeks 1-4)", "tasks": ["Learn proper form", "Establish workout routine", "Track nutrition", "Build consistency"]},
        {"name": "Progressive Overload (Weeks 5-8)", "tasks": ["Increase weights gradually", "Add cardio sessions", "Optimize recovery", "Mid-point assessment"]},
        {"name": "Peak Performance (Weeks 9-12)", "tasks": ["Push personal records", "Fine-tune nutrition", "Final measurements", "Plan maintenance"]}
      ]
    }
  ],
  "quests": [
    {"title": "30-Day Consistency Challenge", "description": "Complete 30 workouts in 30 days", "category": "personal", "goals": [{"actionVerb": "Complete", "what": "workout sessions", "quantity": 30}]}
  ]
}

This program combines daily habits, a structured project with phases, and a challenge quest. Ready to start your fitness journey?`,
    delay: 2500
  },
  {
    keywords: ['learn', 'study', 'education', 'course', 'skill'],
    response: `I'll help you create a structured learning plan! Here's a comprehensive approach:

{
  "type": "program",
  "name": "Continuous Learning System",
  "description": "Develop new skills systematically",
  "habits": [
    {"name": "Daily reading", "description": "30 minutes focused reading", "category": "intellectual", "interval": "daily", "icon": "BookOpen"},
    {"name": "Practice sessions", "description": "Apply what you learned", "category": "intellectual", "interval": "daily", "icon": "Code"}
  ],
  "tasks": [
    {"title": "Choose learning topic", "description": "Research and select your focus area", "category": "intellectual", "priority": "high", "duration": 30},
    {"title": "Find quality resources", "description": "Books, courses, tutorials", "category": "intellectual", "priority": "high", "duration": 60},
    {"title": "Create study schedule", "description": "Block time for learning", "category": "intellectual", "priority": "medium", "duration": 30},
    {"title": "Set up learning space", "description": "Organize physical/digital workspace", "category": "intellectual", "priority": "medium", "duration": 45}
  ],
  "projects": [
    {
      "name": "Skill Mastery Project",
      "description": "Deep dive into your chosen subject",
      "category": "intellectual",
      "phases": [
        {"name": "Foundation", "tasks": ["Complete beginner materials", "Take notes", "Build vocabulary"]},
        {"name": "Practice", "tasks": ["Apply concepts", "Build projects", "Solve problems"]},
        {"name": "Mastery", "tasks": ["Teach others", "Create content", "Tackle advanced topics"]}
      ]
    }
  ]
}

This creates a learning ecosystem with daily habits and a structured project. What specific skill are you interested in developing?`,
    delay: 2000
  },
  {
    keywords: ['productivity', 'organize', 'time management', 'efficient'],
    response: `Let me help you build a productivity system:

{
  "type": "program",
  "name": "Peak Productivity System",
  "description": "Master your time and energy",
  "templates": [
    {
      "name": "Productive Weekday",
      "blocks": [
        {"startTime": "06:00", "endTime": "07:00", "title": "Morning Routine", "category": "personal"},
        {"startTime": "07:00", "endTime": "08:00", "title": "Planning & Priorities", "category": "occupational"},
        {"startTime": "08:00", "endTime": "12:00", "title": "Deep Work Block", "category": "occupational"},
        {"startTime": "12:00", "endTime": "13:00", "title": "Lunch & Movement", "category": "personal"},
        {"startTime": "13:00", "endTime": "17:00", "title": "Collaborative Work", "category": "occupational"},
        {"startTime": "17:00", "endTime": "18:00", "title": "Review & Admin", "category": "occupational"},
        {"startTime": "18:00", "endTime": "22:00", "title": "Personal Time", "category": "avocational"},
        {"startTime": "22:00", "endTime": "23:00", "title": "Evening Wind-down", "category": "personal"}
      ]
    }
  ],
  "habits": [
    {"name": "Daily planning", "description": "Set top 3 priorities", "category": "occupational", "interval": "daily", "icon": "ListChecks"},
    {"name": "Weekly review", "description": "Reflect and plan ahead", "category": "occupational", "interval": "weekly", "icon": "Calendar"}
  ],
  "tasks": [
    {"title": "Clean up workspace", "description": "Organize physical and digital spaces", "category": "occupational", "priority": "medium", "duration": 60},
    {"title": "Set up task system", "description": "Choose tools and methods", "category": "occupational", "priority": "high", "duration": 45},
    {"title": "Define success metrics", "description": "What does productivity mean to you?", "category": "occupational", "priority": "high", "duration": 30}
  ]
}

This system includes time blocking, daily habits, and foundational tasks. Shall we customize it for your specific needs?`,
    delay: 2200
  },
  {
    keywords: ['project', 'plan', 'manage', 'organize work'],
    response: `I'll help you structure your project! Here's a comprehensive project framework:

{
  "type": "project",
  "name": "Strategic Project Plan",
  "description": "Well-organized project with clear phases",
  "category": "occupational",
  "phases": [
    {
      "name": "Discovery & Planning",
      "tasks": [
        "Define project scope",
        "Identify stakeholders",
        "Research requirements",
        "Create timeline",
        "Set budget/resources"
      ]
    },
    {
      "name": "Design & Architecture",
      "tasks": [
        "Create detailed specifications",
        "Design system architecture",
        "Plan technical approach",
        "Review with team",
        "Get approvals"
      ]
    },
    {
      "name": "Development & Execution",
      "tasks": [
        "Build core features",
        "Implement functionality",
        "Regular testing",
        "Iterate based on feedback",
        "Document progress"
      ]
    },
    {
      "name": "Testing & Quality",
      "tasks": [
        "Comprehensive testing",
        "Bug fixes",
        "Performance optimization",
        "User acceptance testing",
        "Final adjustments"
      ]
    },
    {
      "name": "Launch & Delivery",
      "tasks": [
        "Deployment preparation",
        "Go-live execution",
        "Monitor performance",
        "Gather feedback",
        "Create handoff documentation"
      ]
    }
  ]
}

This gives you a solid project structure with 5 phases. What type of project are you working on? I can tailor it to your specific needs.`,
    delay: 1800
  },
  {
    keywords: ['ability', 'skill tree', 'master', 'level up', 'progression'],
    response: `Let me create a skill progression system for you:

{
  "type": "ability",
  "name": "Professional Development Path",
  "description": "Systematic skill mastery progression",
  "category": "occupational",
  "skills": [
    {
      "level": 1,
      "skills": [
        {
          "name": "Foundational Knowledge",
          "description": "Core concepts and terminology",
          "requirements": [
            {"title": "Complete beginner course", "type": "task"},
            {"title": "Study 10 hours", "type": "habit"}
          ]
        }
      ]
    },
    {
      "level": 2,
      "skills": [
        {
          "name": "Basic Application",
          "description": "Apply concepts in simple scenarios",
          "requirements": [
            {"title": "Complete 3 practice projects", "type": "project"},
            {"title": "Daily practice habit", "type": "habit"}
          ]
        }
      ]
    },
    {
      "level": 3,
      "skills": [
        {
          "name": "Intermediate Proficiency",
          "description": "Handle complex situations",
          "requirements": [
            {"title": "Build real-world project", "type": "project"},
            {"title": "Solve 50 problems", "type": "quest"}
          ]
        }
      ]
    },
    {
      "level": 4,
      "skills": [
        {
          "name": "Advanced Techniques",
          "description": "Master sophisticated approaches",
          "requirements": [
            {"title": "Complete advanced course", "type": "task"},
            {"title": "Mentor 2 beginners", "type": "task"}
          ]
        }
      ]
    },
    {
      "level": 5,
      "skills": [
        {
          "name": "Expert Level",
          "description": "Deep understanding and optimization",
          "requirements": [
            {"title": "Contribute to community", "type": "quest"},
            {"title": "Create original content", "type": "project"}
          ]
        }
      ]
    },
    {
      "level": 6,
      "skills": [
        {
          "name": "Thought Leadership",
          "description": "Innovate and lead others",
          "requirements": [
            {"title": "Publish research/articles", "type": "project"},
            {"title": "Speak at events", "type": "quest"}
          ]
        }
      ]
    },
    {
      "level": 7,
      "skills": [
        {
          "name": "Mastery",
          "description": "Recognized expert in field",
          "requirements": [
            {"title": "Build comprehensive portfolio", "type": "project"},
            {"title": "Establish reputation", "type": "quest"}
          ]
        }
      ]
    }
  ]
}

This creates a 7-level skill tree with clear progression. What skill or ability would you like to develop?`,
    delay: 2500
  },
  {
    keywords: ['quest', 'challenge', 'goal', 'achievement'],
    response: `I'll create an engaging quest for you:

{
  "type": "quest",
  "title": "30-Day Transformation Quest",
  "description": "Build momentum through consistent action",
  "category": "personal",
  "goals": [
    {"actionVerb": "Complete", "what": "daily tasks", "quantity": 30},
    {"actionVerb": "Maintain", "what": "habit streak", "quantity": 30},
    {"actionVerb": "Finish", "what": "project milestones", "quantity": 3},
    {"actionVerb": "Achieve", "what": "personal records", "quantity": 5}
  ]
}

This quest will track multiple achievement types. You could also try:
- "100 Hours of Mastery" - dedicate 100 hours to skill development
- "Social Connection Challenge" - strengthen 10 relationships
- "Creative Sprint" - produce 20 pieces of creative work
- "Health Milestone" - achieve specific fitness/wellness goals

Which type of challenge appeals to you most?`,
    delay: 1500
  },
  {
    keywords: ['habit', 'daily', 'weekly', 'build', 'consistency'],
    response: `Let me suggest some powerful habits:

{
  "type": "habits",
  "items": [
    {"name": "Morning journaling", "description": "5 minutes of reflection", "category": "personal", "interval": "daily", "icon": "BookOpen"},
    {"name": "Evening gratitude", "description": "Note 3 things you're grateful for", "category": "existential", "interval": "daily", "icon": "Heart"},
    {"name": "Movement break", "description": "5 minute stretch or walk", "category": "personal", "interval": "daily", "icon": "Activity"},
    {"name": "Learning time", "description": "30 minutes study or read", "category": "intellectual", "interval": "daily", "icon": "Brain"},
    {"name": "Social connection", "description": "Reach out to someone", "category": "social", "interval": "daily", "icon": "Users"}
  ]
}

These habits cover multiple domains and take less than an hour total. Would you like to focus on specific areas? I can suggest:
- Health & Wellness habits
- Productivity habits
- Relationship habits
- Creative habits
- Spiritual/Mindfulness habits

What interests you most?`,
    delay: 1600
  },
  {
    keywords: ['help', 'what can you do', 'capabilities', 'features'],
    response: `I'm your METANOIA AI assistant! I can help you create:

🎯 **Tasks** - Individual action items with priorities and time estimates
📅 **Habits** - Daily or weekly behaviors to build consistency
⏱️ **Routines** - Timed sequences combining multiple habits
📊 **Projects** - Complex initiatives with phases and milestones
🌟 **Abilities** - Skill trees with 7 levels of mastery
🏆 **Quests** - Goal-based challenges with specific targets
📋 **Templates** - Pre-configured daily schedules
🎁 **Complete Programs** - Combinations of all the above!

I work across 7 life domains:
- Personal (self-care)
- Social (relationships)
- Occupational (career)
- Intellectual (learning)
- Avocational (hobbies)
- Ecological (environment)
- Existential (meaning/purpose)

Just tell me what you want to accomplish, and I'll create a structured plan. Try asking things like:
- "Help me build a morning routine"
- "Create a fitness program"
- "Plan a learning project"
- "Design a productivity system"

What would you like help with?`,
    delay: 1000
  }
];

const FALLBACK_RESPONSES = [
  "That's an interesting request! Could you provide more details about what you'd like to accomplish? For example, are you looking to create tasks, habits, routines, projects, abilities, or quests?",
  "I'd love to help with that! To give you the best suggestions, could you tell me:\n- What's your main goal?\n- Which life domain is this related to? (personal, social, occupational, intellectual, avocational, ecological, or existential)\n- What's your timeline?",
  "Great question! I can help you create structured plans. What specific outcome are you hoping to achieve? The more details you share, the better I can tailor my suggestions.",
  "I'm here to help you organize your goals! Would you like me to:\n- Break down a big goal into tasks?\n- Create a daily/weekly habit?\n- Design a routine with timed activities?\n- Plan a multi-phase project?\n- Build a skill progression tree?",
];

export class MockAIService {
  private requestCount = 0;
  private lastRequestTime = 0;

  async chat(messages: AIMessage[]): Promise<AIResponse> {
    const lastMessage = messages[messages.length - 1];

    if (lastMessage.role !== 'user') {
      throw new Error('Last message must be from user');
    }

    const userMessage = lastMessage.content.toLowerCase();

    const matchedTemplate = MOCK_RESPONSES.find(template =>
      template.keywords.some(keyword => userMessage.includes(keyword))
    );

    const delay = matchedTemplate?.delay || 1500;

    await new Promise(resolve => setTimeout(resolve, delay));

    this.requestCount++;
    this.lastRequestTime = Date.now();

    const response = matchedTemplate
      ? matchedTemplate.response
      : FALLBACK_RESPONSES[Math.floor(Math.random() * FALLBACK_RESPONSES.length)];

    const mockTokens = Math.floor(response.length / 4) + Math.floor(lastMessage.content.length / 4);

    return {
      content: response,
      tokensUsed: mockTokens
    };
  }

  getUsageStats() {
    return {
      requestCount: this.requestCount,
      lastRequestTime: this.lastRequestTime
    };
  }

  resetUsageStats() {
    this.requestCount = 0;
    this.lastRequestTime = 0;
  }
}

export const mockAIService = new MockAIService();
